/**
 * Permission Service
 *
 * Handles API calls related to user permissions and company user management.
 */

import api from './api';
import { Company, CompanyUser } from '../types';

export interface PermissionContextResponse {
  user: any;
  companyUser: CompanyUser;
  permissions: {
    features: string[];
    migrated: boolean;
    needsMigration: boolean;
  };
  companyFeatures?: string[];
  appAccess?: {
    adminApp?: {
      hasAccess: boolean;
      denialReason: string | null;
    };
  };
}

/**
 * Get current user's permission context
 */
export const getPermissionContext = async (company :Company | null= null): Promise<PermissionContextResponse> => {
  const response = await api.get('/auth/permission-context', {
    params: { companyId: company?._id }
  });
  return response.data;
};

/**
 * Get current company user details with permissions
 */
export const getCurrentCompanyUser = async (): Promise<CompanyUser> => {
  const response = await api.get('/company-users/current');
  return response.data;
};

/**
 * Update user's feature permissions (admin only)
 */
export const updateUserPermissions = async (
  userId: string,
  featurePermissions: string[]
): Promise<CompanyUser> => {
  const response = await api.put(`/company-users/${userId}/permissions`, {
    featurePermissions
  });
  return response.data;
};

/**
 * Migrate user from old permissions to feature permissions
 */
export const migrateUserPermissions = async (userId?: string): Promise<CompanyUser> => {
  const response = await api.post('/company-users/migrate-permissions', {
    userId: userId || undefined
  });
  return response.data;
};

/**
 * Get available feature permissions
 */
export const getAvailableFeatures = async (): Promise<{
  features: string[];
  categories: any[];
  roleDefaults: Record<string, string[]>;
}> => {
  const response = await api.get('/company-users/available-features');
  return response.data;
};

/**
 * Check if user has specific feature permission
 */
export const checkFeaturePermission = async (feature: string): Promise<boolean> => {
  const response = await api.get(`/company-users/check-permission/${feature}`);
  return response.data.hasPermission;
};

/**
 * Get user's accessible navigation items
 */
export const getAccessibleNavigation = async (): Promise<{
  navigation: any[];
  groups: any[];
  totalItems: number;
}> => {
  const response = await api.get('/company-users/accessible-navigation');
  return response.data;
};

/**
 * Batch check multiple feature permissions
 */
export const checkMultiplePermissions = async (
  features: string[]
): Promise<Record<string, boolean>> => {
  const response = await api.post('/company-users/check-permissions', {
    features
  });
  return response.data.permissions;
};

/**
 * Get permission audit log for user
 */
export const getPermissionAuditLog = async (
  userId?: string,
  limit = 50
): Promise<{
  logs: any[];
  total: number;
}> => {
  const params = new URLSearchParams();
  if (userId) params.append('userId', userId);
  params.append('limit', limit.toString());

  const response = await api.get(`/company-users/permission-audit?${params}`);
  return response.data;
};

/**
 * Export user permissions (admin only)
 */
export const exportUserPermissions = async (format: 'json' | 'csv' = 'json'): Promise<Blob> => {
  const response = await api.get(`/company-users/export-permissions?format=${format}`, {
    responseType: 'blob'
  });
  return response.data;
};

/**
 * Import user permissions (admin only)
 */
export const importUserPermissions = async (file: File): Promise<{
  success: number;
  errors: number;
  details: any[];
}> => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/company-users/import-permissions', formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
  return response.data;
};

/**
 * Get permission templates for roles
 */
export const getPermissionTemplates = async (): Promise<{
  templates: Record<string, string[]>;
  customTemplates: any[];
}> => {
  const response = await api.get('/company-users/permission-templates');
  return response.data;
};

/**
 * Create custom permission template (admin only)
 */
export const createPermissionTemplate = async (
  name: string,
  description: string,
  features: string[]
): Promise<any> => {
  const response = await api.post('/company-users/permission-templates', {
    name,
    description,
    features
  });
  return response.data;
};

/**
 * Apply permission template to user (admin only)
 */
export const applyPermissionTemplate = async (
  userId: string,
  templateId: string
): Promise<CompanyUser> => {
  const response = await api.post(`/company-users/${userId}/apply-template`, {
    templateId
  });
  return response.data;
};